/**
 * A Focusable component that handle the onFocus, onUnfocus, onClickEnter events.
 *
 * Props:
 *   onFocus: (optional)
 *     A function that will be fired when the component is focused.
 *
 *   onUnfocus: (optional)
 *     A function that will be fired when the component is unfocused.
 *
 *   onClickEnter: (optional)
 *     A function that will be fired when the component is focused and enter key is pressed.
 */
import * as React from "react";
import {
  PropsWithChildren,
  FocusEvent,
  KeyboardEvent,
  CSSProperties,
  HTMLAttributes,
} from "react";

import { useFocusPathContext } from "./FocusableSection";
import { useSpatialNavigation } from "./SpatialNavigation";
export interface NavigationFailedEvent {
  target: HTMLElement;
  detail: { direction: "up" | "down" | "left" | "right" };
}

export declare type FocusableProps = PropsWithChildren<
  {
    component?: string;
    className?: string;
    disabled?: boolean;
    onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
    onNavigationFailed?: (e: NavigationFailedEvent) => void;
    onBlur?: (e: FocusEvent<HTMLDivElement>) => void;
    onPressEnter?: (e: KeyboardEvent<HTMLDivElement>) => void;
    style?: CSSProperties;
  } & HTMLAttributes<HTMLDivElement>
>;

const Focusable = (props: FocusableProps) => {
  const {
    component,
    style,
    className,
    disabled,
    onFocus,
    onBlur,
    onPressEnter,
    children,
    onNavigationFailed,
  } = props;
  const ref = React.useRef<HTMLDivElement | null>(null);
  const Component = (component || "div") as any;

  const {
    onNavigationFailed: onNavigationFailedSection,
    sectionId,
    focusKey: sectionFocusKey,
  } = useFocusPathContext();

  const { active } = useSpatialNavigation();
  React.useEffect(() => {
    const el = ref.current as any;

    const navigatefailed =
      onNavigationFailed || onNavigationFailedSection
        ? (e: any) => {
            if (e.target === ref.current) {
              if (onNavigationFailed) {
                onNavigationFailed(e);
              }
              if (onNavigationFailedSection) {
                onNavigationFailedSection(e);
              }
            }
          }
        : undefined;

    if (el && navigatefailed) {
      el.addEventListener("sn:navigatefailed", navigatefailed);
    }
    return () => {
      if (el) {
        el.removeEventListener("sn:navigatefailed", navigatefailed);
      }
    };
  }, [onNavigationFailed, onNavigationFailedSection, sectionFocusKey]);

  // focus
  React.useEffect(() => {
    const el = ref.current as any;

    const _onFocus = onFocus
      ? (e: React.FocusEvent<HTMLDivElement>) => {
          if (e.target === ref.current) {
            onFocus(e);
          }
        }
      : undefined;

    if (el && onFocus) {
      el.addEventListener("sn:focused", _onFocus);
    }
    return () => {
      if (el) {
        el.removeEventListener("sn:focused", _onFocus);
      }
    };
  }, [onFocus]);

  // blur
  React.useEffect(() => {
    const el = ref.current as any;

    const _onBlur = onBlur
      ? (e: React.FocusEvent<HTMLDivElement>) => {
          if (e.target === ref.current) {
            onBlur(e);
          }
        }
      : undefined;

    if (el && onBlur) {
      el.addEventListener("sn:unfocused", _onBlur);
    }
    return () => {
      if (el) {
        el.removeEventListener("sn:unfocused", _onBlur);
      }
    };
  }, [onBlur]);

  // Enter
  React.useEffect(() => {
    const el = ref.current as any;

    const _onPressEnter = onPressEnter
      ? (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.target === ref.current) {
            onPressEnter(e);
          }
        }
      : undefined;

    if (el && onPressEnter) {
      el.addEventListener("sn:enter-up", _onPressEnter);
    }
    return () => {
      if (el) {
        el.removeEventListener("sn:enter-up", _onPressEnter);
      }
    };
  }, [onPressEnter]);

  const { config } = useSpatialNavigation();

  const classNames = [sectionId ? `${sectionId}` : config && config.focusableClassName];
  if (className) {
    classNames.push(className);
  }
  const remainingProps: Partial<FocusableProps> = { ...props };
  delete remainingProps.onPressEnter;
  delete remainingProps.onFocus;
  delete remainingProps.onBlur;
  delete remainingProps.onNavigationFailed;
  delete remainingProps.component;

  //Prevent from triggering the onClick & the onPressEnter event
  if (active && props.onPressEnter && Component === "button") {
    delete remainingProps.onPressEnter;
  }
  return (
    <Component
      {...remainingProps}
      className={classNames.join(" ")}
      aria-disabled={!!disabled}
      ref={ref}
      tabIndex={-1}
      style={style}
    >
      {children}
    </Component>
  );
};

export default Focusable;
